import {
  Option,
  LabelToPaginatingMapping,
  StateToLabelMapping
} from "../components/AppFilters/Types/index"
import actionCreatorFactory from "typescript-fsa"
import { reducerWithoutInitialState } from "typescript-fsa-reducers"
import { District, Educator, Student } from "../../mp-database"
import firebase from "firebase/compat/app"
import "firebase/auth"
import "firebase/firestore"

export const actionCreator = actionCreatorFactory("KeyReveal")

export const setDistrictRecord = actionCreator<District>("setDistrictRecord")
export const setUserRecord = actionCreator<Educator | Student>("setUserRecord")
export const setFbUser = actionCreator<firebase.User | null>("setFbUser")

export type DistrictState = {
  record: District
}

export type UserState = {
  fbUser: firebase.User | null
  record: Educator | Student
}

export const DistrictReducer = reducerWithoutInitialState<DistrictState>()
  .case(setDistrictRecord, (state, record) => {
    // console.log(`District: ${JSON.stringify(record, null, 2)}`)
    return { ...state, record }
  })
  .build()

export const UserReducer = reducerWithoutInitialState<UserState>()
  .case(setFbUser, (state, fbUser) => {
    // console.log(`fbUser: ${JSON.stringify(fbUser, null, 2)}`)
    return { ...state, fbUser }
  })
  .case(setUserRecord, (state, record) => {
    // console.log(`User: ${JSON.stringify(record, null, 2)}`)
    return { ...state, record }
  })
  .build()

type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key
      }
    : {
        type: Key
        payload: M[Key]
      }
}

export enum ActionTypeEnums {
  UpdateParams = "UPDATE_PARAMS",
  ClearParams = "CLEAR_PARAMS",
  ChangeTheme = "CHANGE_THEME",
  HideMainHeaderContent = "HIDE_MAIN_HEADER_CONTENT",
  SubtestResetControlLoading = "SUBTEST_RESET_CONTROLS_LOADING"
}

// Product

export type HeaderStateType = {
  sharedParams?: { [key: string]: any }
  hideContent?: boolean
}

type HeaderPayload = {
  [ActionTypeEnums.UpdateParams]: { [key: string]: any }
  [ActionTypeEnums.ClearParams]: {}
  [ActionTypeEnums.HideMainHeaderContent]: boolean
}

export type HeaderActions =
  ActionMap<HeaderPayload>[keyof ActionMap<HeaderPayload>]

export const headerReducer = (
  state: HeaderStateType,
  action: HeaderActions | ThemeActions
) => {
  switch (action.type) {
    case ActionTypeEnums.UpdateParams:
      return {
        ...state,
        sharedParams: { ...state.sharedParams, ...action.payload }
      }
    case ActionTypeEnums.ClearParams:
      return { ...state, sharedParams: {} }
    case ActionTypeEnums.HideMainHeaderContent:
      return { ...state, hideContent: action.payload }
    default:
      return state
  }
}

type ThemeStateType = {
  name?: string
}

type ThemePayload = {
  [ActionTypeEnums.ChangeTheme]: string
}

export type ThemeActions =
  ActionMap<ThemePayload>[keyof ActionMap<ThemePayload>]

export const themeReducer = (
  state: ThemeStateType,
  action: HeaderActions | ThemeActions
) => {
  switch (action.type) {
    case ActionTypeEnums.ChangeTheme:
      return { ...state, name: action.payload }
    default:
      return state
  }
}

export enum AdminFilterActionTypeEnums {
  FilterOptionsAppend = "APP_FILTER_APPEND",
  FilterOptionsModify = "APP_FILTERS_MODIFY",
  FilterOptionsClear = "APP_FILTERS_EMPTY",
  FilterOptionModify = "APP_FILTER_MODIFY"
}

export type AdminFilterStateType = {
  districts: Array<Option>
  schools: Array<Option>
  teachers: Array<Option>
  classes: Array<Option>
  students: Array<Option>

  district?: Option
  school?: Option
  teacher?: Option
  class?: Option
  s?: Option

  isFetchingDistricts: boolean
  isFetchingSchools: boolean
  isFetchingTeachers: boolean
  isFetchingClasses: boolean
  isFetchingStudents: boolean

  isPaginatingDistricts: boolean
  isPaginatingSchools: boolean
  isPaginatingTeachers: boolean
  isPaginatingClasses: boolean
  isPaginatingStudents: boolean
  // districts

  // for pagination
  districtsCursor?: any
  schoolsCursor?: any
  teachersCursor?: any
  classesCursor?: any
  studentsCursor?: any

  isDistrictWithSingleSchool?: boolean

  optionsKey?: string

  error: string
}

type AdminFilterPayload = {
  [AdminFilterActionTypeEnums.FilterOptionsAppend]: {
    optionsKey: string
  }
  [AdminFilterActionTypeEnums.FilterOptionsModify]: {}
  [AdminFilterActionTypeEnums.FilterOptionsClear]: {}
  [AdminFilterActionTypeEnums.FilterOptionModify]: {}
}

export type AdminFilterActions =
  ActionMap<AdminFilterPayload>[keyof ActionMap<AdminFilterPayload>]

export const adminFilterReducer = (
  state: AdminFilterStateType,
  action: AdminFilterActions
) => {
  switch (action.type) {
    case AdminFilterActionTypeEnums.FilterOptionsAppend:
      const optionKey = action.payload.optionsKey
      const isPaginatingKey =
        LabelToPaginatingMapping[StateToLabelMapping[optionKey]]

      if (action.payload[isPaginatingKey] === true) {
        return { ...state, [isPaginatingKey]: true }
      } else {
        return {
          ...state,
          ...action.payload,
          [optionKey]: [...state[optionKey], ...action.payload[optionKey]]
        }
      }
    case AdminFilterActionTypeEnums.FilterOptionsModify:
      return Object.assign({}, state, action.payload)

    case AdminFilterActionTypeEnums.FilterOptionsClear:
      return {
        ...state,
        filters: {}
      }
    case AdminFilterActionTypeEnums.FilterOptionModify:
      return {
        ...state,
        ...action.payload
      }
    default:
      return state
  }
}

export type SubtestResetControlsStateType = {
  isPerformingAction: boolean
}

type SubtestResetControlsPayload = {
  [ActionTypeEnums.SubtestResetControlLoading]: boolean
}

export type SubtestResetControlsActions =
  ActionMap<SubtestResetControlsPayload>[keyof ActionMap<SubtestResetControlsPayload>]

export const subtestResetControlsReducer = (
  state: SubtestResetControlsStateType,
  action: HeaderActions | ThemeActions | SubtestResetControlsActions
) => {
  switch (action.type) {
    case ActionTypeEnums.SubtestResetControlLoading:
      return { ...state, isPerformingAction: action.payload }
    default:
      return state
  }
}
